import {css, useTheme} from 'styled-components';

import {AriaRole, CSSProperties, ForwardedRef, forwardRef, ReactNode} from 'react';

// eslint-disable-next-line no-restricted-imports -- Intended
import {path} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {CSSDimension} from '../../types/CSSDimension';
import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {
  ThemeColorPath,
  ThemeRadiusPath,
  ThemeShadowsPath,
  ThemeZIndexPath,
} from '../../utils/foundationTheme';
import {getCssSize} from '../../utils/getCssSize';
import {getShadowByPath} from '../../utils/getShadowByPath';
import {getSize} from '../../utils/getSize';

export interface BoxProps extends TestIdProps {
  ref?: ForwardedRef<HTMLDivElement>;
  children?: ReactNode;
  role?: AriaRole;
  id?: string;
  backgroundColor?: ThemeColorPath | ValueByDevice<ThemeColorPath | undefined>;
  /**
   * @about Multiplier of the design system's base step
   */
  padding?: Integer | ValueByDevice<Integer>;
  paddingHorizontal?: Integer | ValueByDevice<Integer>;
  paddingVertical?: Integer | ValueByDevice<Integer>;
  paddingTop?: Integer | ValueByDevice<Integer>;
  paddingLeft?: Integer | ValueByDevice<Integer>;
  paddingRight?: Integer | ValueByDevice<Integer>;
  paddingBottom?: Integer | ValueByDevice<Integer>;
  /**
   * Define only width and style
   * Use borderColor to set color
   * @example border="1px solid"
   */
  border?: string | ValueByDevice<string | undefined>;
  borderBottom?: string | ValueByDevice<string | undefined>;
  borderLeft?: string | ValueByDevice<string | undefined>;
  borderRight?: string | ValueByDevice<string | undefined>;
  borderTop?: string | ValueByDevice<string | undefined>;
  borderColor?: ThemeColorPath;
  borderRadius?: ThemeRadiusPath | ValueByDevice<ThemeRadiusPath | undefined>;
  borderTopLeftRadius?: ThemeRadiusPath | ValueByDevice<ThemeRadiusPath | undefined>;
  borderTopRightRadius?: ThemeRadiusPath | ValueByDevice<ThemeRadiusPath | undefined>;
  borderBottomLeftRadius?: ThemeRadiusPath | ValueByDevice<ThemeRadiusPath | undefined>;
  borderBottomRightRadius?: ThemeRadiusPath | ValueByDevice<ThemeRadiusPath | undefined>;
  boxShadow?: ThemeShadowsPath;
  position?: CSSProperties['position'] | ValueByDevice<CSSProperties['position'] | undefined>;
  bottom?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  left?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  right?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  top?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  width?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  height?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  maxHeight?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  maxWidth?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  minHeight?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  minWidth?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  zIndex?: ThemeZIndexPath;
  flex?: number | ValueByDevice<number | undefined>;
  flexGrow?: number | ValueByDevice<number | undefined>;
  flexShrink?: number | ValueByDevice<number | undefined>;
  flexBasis?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  overflow?: 'auto' | 'hidden' | 'scroll';
  overflowY?: 'auto' | 'hidden' | 'scroll';
  overflowX?: 'auto' | 'hidden' | 'scroll';
  opacity?: number;
  contain?: CSSProperties['contain'];
  transition?: string;
  pointerEvents?: CSSProperties['pointerEvents'];
  ratio?: CSSProperties['aspectRatio'];
  tabIndex?: number;
}

function BoxComponent(props: BoxProps, ref: ForwardedRef<HTMLDivElement>) {
  const theme = useTheme();

  const backgroundColor = useResponsivePropValue(props.backgroundColor);
  const padding = useResponsivePropValue(props.padding);
  const paddingVertical = useResponsivePropValue(props.paddingVertical);
  const paddingHorizontal = useResponsivePropValue(props.paddingHorizontal);
  const paddingLeft = useResponsivePropValue(props.paddingLeft);
  const paddingRight = useResponsivePropValue(props.paddingRight);
  const paddingTop = useResponsivePropValue(props.paddingTop);
  const paddingBottom = useResponsivePropValue(props.paddingBottom);
  const border = useResponsivePropValue(props.border);
  const borderBottom = useResponsivePropValue(props.borderBottom);
  const borderLeft = useResponsivePropValue(props.borderLeft);
  const borderRight = useResponsivePropValue(props.borderRight);
  const borderTop = useResponsivePropValue(props.borderTop);
  const borderRadius = useResponsivePropValue(props.borderRadius);
  const borderTopLeftRadius = useResponsivePropValue(props.borderTopLeftRadius);
  const borderTopRightRadius = useResponsivePropValue(props.borderTopRightRadius);
  const borderBottomLeftRadius = useResponsivePropValue(props.borderBottomLeftRadius);
  const borderBottomRightRadius = useResponsivePropValue(props.borderBottomRightRadius);
  const width = useResponsivePropValue(props.width);
  const height = useResponsivePropValue(props.height);
  const maxWidth = useResponsivePropValue(props.maxWidth);
  const maxHeight = useResponsivePropValue(props.maxHeight);
  const minWidth = useResponsivePropValue(props.minWidth);
  const minHeight = useResponsivePropValue(props.minHeight);
  const flex = useResponsivePropValue(props.flex);
  const flexGrow = useResponsivePropValue(props.flexGrow);
  const flexShrink = useResponsivePropValue(props.flexShrink);
  const flexBasis = useResponsivePropValue(props.flexBasis);
  const top = useResponsivePropValue(props.top);
  const left = useResponsivePropValue(props.left);
  const right = useResponsivePropValue(props.right);
  const bottom = useResponsivePropValue(props.bottom);
  const position = useResponsivePropValue(props.position);

  const token = (key: string) => path<string>(key.split('.'), theme) ?? '';

  return (
    <div
      ref={ref}
      role={props.role}
      id={props.id}
      tabIndex={props.tabIndex}
      css={css`
        flex-grow: ${flexGrow ?? flex ?? 'initial'};
        flex-shrink: ${flexShrink ?? flex ?? 'initial'};
        flex-basis: ${flexBasis ? getCssSize(flexBasis) : isNotNil(flex) ? '0%' : 'initial'};

        padding-top: ${getSize(paddingTop ?? paddingVertical ?? padding ?? 0)};
        padding-left: ${getSize(paddingLeft ?? paddingHorizontal ?? padding ?? 0)};
        padding-right: ${getSize(paddingRight ?? paddingHorizontal ?? padding ?? 0)};
        padding-bottom: ${getSize(paddingBottom ?? paddingVertical ?? padding ?? 0)};

        border-bottom: ${borderBottom};
        border-left: ${borderLeft};
        border-right: ${borderRight};
        border-top: ${borderTop};
        border: ${border};
        border-color: ${token('colors.' + props.borderColor)};
        border-radius: ${token('radii.' + borderRadius)};
        border-top-left-radius: ${token('radii.' + borderTopLeftRadius)};
        border-top-right-radius: ${token('radii.' + borderTopRightRadius)};
        border-bottom-left-radius: ${token('radii.' + borderBottomLeftRadius)};
        border-bottom-right-radius: ${token('radii.' + borderBottomRightRadius)};

        top: ${getCssSize(top)};
        left: ${getCssSize(left)};
        right: ${getCssSize(right)};
        bottom: ${getCssSize(bottom)};
        position: ${position};

        height: ${getCssSize(height)};
        width: ${getCssSize(width)};
        max-height: ${getCssSize(maxHeight)};
        min-height: ${getCssSize(minHeight)};
        max-width: ${getCssSize(maxWidth)};
        min-width: ${getCssSize(minWidth)};
        aspect-ratio: ${props.ratio ?? 'initial'};

        box-shadow: ${getShadowByPath(props.boxShadow, theme)};
        background-color: ${token(`colors.${backgroundColor}`)};
        opacity: ${props.opacity};

        z-index: ${token('zIndices.' + props.zIndex)};
        contain: ${props.contain};
        overflow: ${props.overflow};
        overflow-y: ${props.overflowY};
        overflow-x: ${props.overflowX};
        transition: ${props.transition};
        pointer-events: ${props.pointerEvents};

        &:focus-visible {
          outline: ${token('outline.default')}
            ${token(`colors.${backgroundColor}`) ?? 'currentColor'};
          outline-offset: ${token('outline.offset')};
        }
      `}
      data-testid={suffixTestId('box', props)}
    >
      {props.children}
    </div>
  );
}

export const Box = forwardRef(BoxComponent);
